"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.RestInternalError = exports.RestTransformError = exports.RestResponseError = exports.RestAbortManager = exports.isFileType = exports.combineHeaders = exports.combinePath = exports.RestResponse = exports.RestAdapter = exports.RestContentType = exports.RestClient = void 0;
const is_blank_1 = __importDefault(require("@sedan-utils/is-blank"));
const qs_1 = __importDefault(require("qs"));
const JsonUtil_1 = require("./JsonUtil");
const LangUtil_1 = require("./LangUtil");
const GenUtil_1 = require("./GenUtil");
/**
 * 如果状态码不是 200-299, 会抛出异常 [RestResponseError]
 * 如果 JSON 转 Model 出现错误，会抛出异常 [RestTransformError]
 */
class RestClient {
    constructor(adapter, abortManager = undefined, fetchImpl = (...args) => window.fetch(...args)) {
        this.adapter = adapter;
        this.abortManager = abortManager;
        this.fetchImpl = fetchImpl;
    }
    async request(params) {
        const adapter = await (async () => this.adapter)();
        let contentType = adapter.contentType;
        if (params.contentType) {
            contentType = params.contentType;
        }
        let requestInit = {
            method: params.method,
            headers: combineHeaders(adapter.headers, params.headers),
        };
        if (contentType) {
            delete requestInit.headers["Content-Type"];
        }
        if (params.data) {
            let body = params.data;
            if (params.data instanceof FormData) {
                body = params.data;
            }
            else {
                if (contentType == RestContentType.json) {
                    requestInit.headers["Content-Type"] = contentType;
                    if (typeof params.data !== "string") {
                        body = JsonUtil_1.JsonUtil.stringify(params.data);
                    }
                    else {
                        body = params.data;
                    }
                }
                else if (contentType == RestContentType.form) {
                    requestInit.headers["Content-Type"] = contentType;
                    if (typeof params.data !== "string") {
                        body = JsonUtil_1.JsonUtil.stringify(params.data);
                    }
                    else {
                        body = params.data;
                    }
                }
                else if (!contentType) {
                    // temporary use JSON, but we should be able to determine the content type from body later
                    requestInit.headers["Content-Type"] = RestContentType.json;
                    if (typeof params.data !== "string") {
                        body = JsonUtil_1.JsonUtil.stringify(params.data);
                    }
                    else {
                        body = params.data;
                    }
                }
            }
            requestInit = { ...requestInit, body: body };
        }
        let uuid = params.uuid || GenUtil_1.GenUtil.randomString();
        const abortController = new AbortController();
        if (this.abortManager) {
            this.abortManager.add(uuid, abortController, params);
        }
        requestInit = { ...requestInit, signal: abortController.signal };
        let url = combinePath(params.path, params.queryParameters);
        if (!(url.startsWith("http://") || url.startsWith("https://"))) {
            url = adapter.baseUrl + url;
        }
        const result = await (0, LangUtil_1.tryPromise)(this.fetchImpl(url, requestInit));
        if (this.abortManager) {
            this.abortManager.remove(uuid);
        }
        return await handleResponse(result, adapter);
    }
    async get(path, queryParameters, headers, contentType = undefined, uuid) {
        return this.request({
            path,
            method: "GET",
            headers,
            queryParameters,
            contentType,
            uuid,
        });
    }
    async post(path, data, queryParameters, headers, contentType = undefined, uuid) {
        return this.request({
            path,
            method: "POST",
            headers,
            data,
            queryParameters,
            contentType,
            uuid,
        });
    }
    async put(path, data, queryParameters, headers, contentType = undefined) {
        return this.request({
            path,
            method: "PUT",
            headers,
            data,
            queryParameters,
            contentType,
        });
    }
    async patch(path, data, queryParameters, headers, contentType = undefined) {
        return this.request({
            path,
            method: "PATCH",
            headers,
            data,
            queryParameters,
            contentType,
        });
    }
    async delete(path, data, queryParameters, headers, contentType = undefined) {
        return this.request({
            path,
            method: "DELETE",
            headers,
            data,
            queryParameters,
            contentType,
        });
    }
}
exports.RestClient = RestClient;
var RestContentType;
(function (RestContentType) {
    RestContentType["form"] = "application/x-www-form-urlencoded;charset=UTF-8";
    RestContentType["json"] = "application/json;charset=UTF-8";
    RestContentType["any"] = "";
})(RestContentType || (exports.RestContentType = RestContentType = {}));
class RestAdapter {
    errorHandler(error) { }
    get baseUrl() {
        return "";
    }
    get headers() {
        return {};
    }
    get contentType() {
        return RestContentType.form;
    }
}
exports.RestAdapter = RestAdapter;
class RestResponse {
    constructor(status, message, headers, data) {
        this.status = status;
        this.message = message;
        this.headers = headers;
        this.data = data;
    }
    parseError(handler) {
        this.parseErrorHandler = response => handler(response);
        return this;
    }
    get errorDescription() {
        if (this.parseErrorHandler != null) {
            const message = this.parseErrorHandler(this);
            if (!(0, is_blank_1.default)(message)) {
                return message;
            }
        }
        return undefined;
    }
    get isSuccess() {
        return this.status >= 200 && this.status < 300;
    }
    validate() {
        if (!this.isSuccess) {
            throw new RestResponseError(this);
        }
        return this;
    }
    valueOf(value) {
        const newResponse = new RestResponse(this.status, this.message, this.headers, value);
        newResponse.parseErrorHandler = this.parseErrorHandler;
        return newResponse;
    }
    toModel(cls, extract) {
        this.validate();
        const source = extract(this);
        const newValue = JsonUtil_1.JsonUtil.toModelFromType(cls, source);
        if (!newValue) {
            throw new RestTransformError(cls, source);
        }
        return this.valueOf(newValue);
    }
    toModelFromType(cls) {
        this.validate();
        const newValue = JsonUtil_1.JsonUtil.toModelFromType(cls, this.data);
        if (!newValue) {
            throw new RestTransformError(cls, this.data);
        }
        return this.valueOf(newValue);
    }
    toModelFromInstance(instance) {
        this.validate();
        const newValue = JsonUtil_1.JsonUtil.toModelFromInstance(instance, this.data);
        if (!newValue) {
            throw new RestTransformError(instance, this.data);
        }
        return this.valueOf(newValue);
    }
    toArray(cls, extract) {
        this.validate();
        const source = extract(this);
        const newValue = JsonUtil_1.JsonUtil.toArrayFromType(cls, source);
        if (!newValue) {
            throw new RestTransformError(cls, source);
        }
        const newResponse = new RestResponse(this.status, this.message, this.headers, newValue);
        newResponse.parseErrorHandler = this.parseErrorHandler;
        return newResponse;
    }
    toArrayFromType(cls) {
        this.validate();
        const newValue = JsonUtil_1.JsonUtil.toArrayFromType(cls, this.data);
        if (!newValue) {
            throw new RestTransformError(cls, this.data);
        }
        const newResponse = new RestResponse(this.status, this.message, this.headers, newValue);
        newResponse.parseErrorHandler = this.parseErrorHandler;
        return newResponse;
    }
}
exports.RestResponse = RestResponse;
function combinePath(path, queryParameters) {
    if (queryParameters == undefined || (0, is_blank_1.default)(queryParameters))
        return path;
    return path + "?" + qs_1.default.stringify(queryParameters);
}
exports.combinePath = combinePath;
function combineHeaders(headers1, headers2, headers3) {
    let headers = {};
    if (!(0, is_blank_1.default)(headers1))
        headers = Object.assign(headers, headers1);
    if (!(0, is_blank_1.default)(headers2))
        headers = Object.assign(headers, headers2);
    if (!(0, is_blank_1.default)(headers3))
        headers = Object.assign(headers, headers3);
    return headers;
}
exports.combineHeaders = combineHeaders;
async function handleResponse(result, adapter) {
    if (result.error) {
        const basicError = new RestInternalError(result.error);
        adapter?.errorHandler(basicError);
        throw basicError;
    }
    const res = result.data;
    if (isFileType(res)) {
        const response = new RestResponse(res.status, res.statusText, res.headers, res);
        if (!response.isSuccess) {
            adapter?.errorHandler(response);
        }
        return response;
    }
    const textResult = await (0, LangUtil_1.tryPromise)(res.text());
    const json = JsonUtil_1.JsonUtil.parse(textResult.data ?? "");
    if (textResult.error) {
        const basicError = new RestInternalError(json.error);
        adapter?.errorHandler(basicError);
        throw basicError;
    }
    const data = JsonUtil_1.JsonUtil.parse(textResult.data) ?? {};
    const response = new RestResponse(res.status, res.statusText, res.headers, data);
    if (!response.isSuccess) {
        adapter?.errorHandler(response);
    }
    return response;
}
/**
 * 是图片，或者html类型的（后续如果碰到还会添加其他的，如果video等），那么是需要走单独的返回response逻辑
 * @param response
 */
function isFileType(response) {
    const fileType = ["image/", "text/html", "audio/mpeg"];
    const result = response.headers.get("Content-Type");
    if (result && fileType.some(item => result.startsWith(item))) {
        return true;
    }
    return false;
}
exports.isFileType = isFileType;
class RestAbortManager {
    constructor() {
        this.requestList = [];
    }
    add(uuid, abortController, request) {
        const requestEntry = new RequestEntry(uuid, abortController, request);
        this.requestList.push(requestEntry);
    }
    remove(uuid) {
        this.requestList = this.requestList.filter(x => x.uuid != uuid);
    }
    abort(params) {
        if (!(0, is_blank_1.default)(params.uuid)) {
            this.requestList.filter(x => x.uuid == params.uuid).forEach(x => x.abortController.abort());
            this.requestList = this.requestList.filter(x => x.uuid != params.uuid);
        }
        const filter = params.filter;
        if (filter != null) {
            const list = this.requestList.filter(x => filter(x.uuid, x.requestInfo));
            this.requestList = this.requestList.filter(x => list.filter(a => x.uuid != x.uuid));
            list.forEach(x => {
                x.abortController.abort();
            });
        }
    }
}
exports.RestAbortManager = RestAbortManager;
class RequestEntry {
    constructor(uuid, abortController, requestInfo) {
        this.uuid = uuid;
        this.abortController = abortController;
        this.requestInfo = requestInfo;
    }
}
class RestResponseError extends RestResponse {
    constructor(response) {
        super(response.status, response.message, response.headers, response.data);
        this.parseErrorHandler = response.parseErrorHandler;
    }
}
exports.RestResponseError = RestResponseError;
class RestTransformError {
    constructor(target, source) {
        this.target = target;
        this.source = source;
    }
}
exports.RestTransformError = RestTransformError;
class RestInternalError {
    constructor(error) {
        this.error = error;
    }
    parseError(handler) {
        this.parseErrorHandler = response => handler(response);
        return this;
    }
    get errorDescription() {
        if (this.parseErrorHandler != null) {
            const message = this.parseErrorHandler(this);
            if (!(0, is_blank_1.default)(message)) {
                return message;
            }
        }
        return undefined;
    }
}
exports.RestInternalError = RestInternalError;
